import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RotateCcw, ArrowRight, UserPlus, Trophy, DollarSign, Hourglass } from 'lucide-react';

const fmtCur = (v) => `AED ${Math.round(v || 0).toLocaleString()}`;

const Kpi = ({ title, value, subtitle, icon: Icon, color, bg, testId }) => (
  <div className="rounded-lg border border-border/50 p-2.5 flex items-start gap-2.5" data-testid={testId}>
    <div className={`p-1.5 rounded-md ${bg}`}><Icon className={`h-3.5 w-3.5 ${color}`} /></div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
      {subtitle && <div className="text-[10px] text-muted-foreground truncate">{subtitle}</div>}
    </div>
  </div>
);

export function ReturningLeadsWidget({ period, customStart, customEnd }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => {
    if (period === 'custom' && !customStart) return;
    const params = { period, ...(period === 'custom' ? { custom_start: customStart, custom_end: customEnd } : {}) };
    apiClient.get('/sales/reinquiries/stats', { params }).then(r => setS(r.data)).catch(() => setS(null));
  }, [period, customStart, customEnd]);
  if (!s) return null;
  const total = s.revenue_verified + s.revenue_pending;
  return (
    <Card className="border border-border/50" data-testid="exec-returning-widget">
      <CardHeader className="pb-1 pt-2.5 px-3">
        <CardTitle className="text-xs font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <RotateCcw className="h-3.5 w-3.5 text-orange-500" />
            Returning Leads
            <span className="text-[10px] text-muted-foreground">· {s.open_now} waiting now · {s.new_in_period} came back in this period · {s.dismissed} dismissed</span>
          </span>
          <Button variant="ghost" size="sm" className="h-6 text-[10px]" data-testid="exec-returning-open" onClick={() => navigate('/sales/returning-leads')}>
            Open Returning Leads <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-2.5 space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi title="Assigned" value={s.assigned} subtitle={s.linked_numbers_moved ? `+${s.linked_numbers_moved} linked number${s.linked_numbers_moved > 1 ? 's' : ''} moved along` : 're-opened as New Lead'} icon={UserPlus} color="text-sky-500" bg="bg-sky-500/10" testId="exec-returning-assigned" />
          <Kpi title="Enrolled" value={s.enrolled} subtitle={`${s.conversion_rate}% conversion`} icon={Trophy} color="text-emerald-500" bg="bg-emerald-500/10" testId="exec-returning-enrolled" />
          <Kpi title="Revenue (verified)" value={fmtCur(s.revenue_verified)} subtitle={total ? `${fmtCur(total)} incl. pending` : 'from returning leads'} icon={DollarSign} color="text-amber-500" bg="bg-amber-500/10" testId="exec-returning-revenue" />
          <Kpi title="Awaiting finance" value={fmtCur(s.revenue_pending)} subtitle="closed, not yet verified" icon={Hourglass} color="text-violet-500" bg="bg-violet-500/10" testId="exec-returning-pending" />
        </div>
        {s.assigned > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg border border-border/50 p-2" data-testid="exec-returning-agents">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">By agent</div>
              <table className="w-full"><tbody>
                {s.agents.slice(0, 6).map(a => (
                  <tr key={a.agent} className="[&>td]:py-0.5">
                    <td className="truncate max-w-[140px]">{a.agent}<span className="text-muted-foreground"> {a.team && `· ${a.team}`}</span></td>
                    <td className="text-right tabular-nums text-muted-foreground">{a.assigned} assigned</td>
                    <td className="text-right tabular-nums">{a.enrolled} enrolled</td>
                    <td className="text-right tabular-nums font-medium">{fmtCur(a.revenue)}</td>
                  </tr>))}
              </tbody></table>
            </div>
            <div className="rounded-lg border border-border/50 p-2" data-testid="exec-returning-conversions">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Pipeline of assigned returning leads</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(s.stage_counts).sort(([, a], [, b]) => b - a).map(([st, n]) => (
                  <span key={st} className={`rounded-full border px-2 py-0.5 text-[10px] ${st === 'enrolled' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : st === 'rejected' || st === 'not_interested' ? 'border-rose-300 text-rose-700 bg-rose-50' : 'border-border text-muted-foreground'}`}>{st.replace(/_/g, ' ')} · {n}</span>))}
              </div>
              {s.conversions.length > 0 && <div className="mt-1.5 space-y-0.5">
                {s.conversions.slice(0, 4).map(c => <div key={c.lead_id} className="flex justify-between"><span className="truncate">{c.full_name} <span className="text-muted-foreground">→ {c.agent}</span></span><span className="font-medium tabular-nums">{fmtCur(c.revenue)}</span></div>)}
              </div>}
              {s.conversions.length === 0 && <div className="mt-1.5 text-muted-foreground">No enrolments yet from returning leads in this period.</div>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
